import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchSensitivityStats,
  fetchFields,
  fetchObjects,
  updateFieldClassification
} from '../../services/api/sensitivityApi';
import { useToast } from '../../contexts/ToastContext';
import {
  StandardSearchBar,
  StandardSelect,
  StandardLoadingState,
  StandardErrorState
} from '../standard';

// Data Sensitivity Manager Component

type SensitivityLevel = 'public' | 'internal' | 'confidential' | 'restricted' | 'highlyRestricted';

const DataSensitivityManager: React.FC = () => {
  const queryClient = useQueryClient();
  const { error: showError } = useToast();

  // State
  const [selectedObject, setSelectedObject] = useState<string>('all');
  const [selectedLevel, setSelectedLevel] = useState<SensitivityLevel | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedFields, setSelectedFields] = useState<string[]>([]);

  // Fetch statistics
  const { data: statsResponse, isLoading: isLoadingStats } = useQuery({
    queryKey: ['sensitivity-stats'],
    queryFn: fetchSensitivityStats,
    staleTime: 60 * 1000
  });

  const stats = statsResponse?.data;

  // Fetch objects for filter
  const { data: objectsResponse } = useQuery({
    queryKey: ['sensitivity-objects'],
    queryFn: fetchObjects,
    staleTime: 300 * 1000
  });

  const objects = objectsResponse?.data || [];

  // Fetch fields
  const { data: fieldsResponse, isLoading: isLoadingFields, error } = useQuery({
    queryKey: ['sensitivity-fields', selectedObject, selectedLevel],
    queryFn: () => fetchFields({
      object: selectedObject !== 'all' ? selectedObject : undefined,
      classification: selectedLevel !== 'all' ? selectedLevel : undefined
    }),
    staleTime: 60 * 1000
  });

  const fields = fieldsResponse?.data || [];

  // Filter by search
  const filteredFields = useMemo(() => {
    if (!searchQuery) return fields;

    const query = searchQuery.toLowerCase();
    return fields.filter((f: any) =>
      f.fieldName.toLowerCase().includes(query) ||
      f.fieldApiName.toLowerCase().includes(query) ||
      f.objectName.toLowerCase().includes(query)
    );
  }, [fields, searchQuery]);

  // Mutation for updating field classification
  const updateMutation = useMutation({
    mutationFn: ({ fieldId, updates }: { fieldId: string; updates: any }) =>
      updateFieldClassification(fieldId, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sensitivity-fields'] });
      queryClient.invalidateQueries({ queryKey: ['sensitivity-stats'] });
      setEditingId(null);
    }
  });

  // Handlers
  const handleUpdate = (fieldId: string, updates: any) => {
    updateMutation.mutate({ fieldId, updates });
  };

  const handleBulkUpdate = () => {
    if (selectedFields.length === 0) {
      showError('No fields selected', 'Please select fields first');
      return;
    }

    const level = window.prompt(
      'Enter sensitivity level for selected fields:\n' +
      '- public\n' +
      '- internal\n' +
      '- confidential\n' +
      '- restricted\n' +
      '- highlyRestricted'
    )?.toLowerCase();

    if (!level || !['public', 'internal', 'confidential', 'restricted', 'highlyRestricted'].includes(level)) {
      showError('Invalid sensitivity level', 'Please enter a valid sensitivity level');
      return;
    }

    if (window.confirm(`Set ${selectedFields.length} field(s) to "${level}"?`)) {
      // Update each field individually (mock API doesn't support true bulk update)
      selectedFields.forEach(fieldId => {
        handleUpdate(fieldId, { classification: level });
      });
      setSelectedFields([]);
    }
  };

  const handleToggleSelection = (fieldId: string) => {
    setSelectedFields(prev =>
      prev.includes(fieldId)
        ? prev.filter(id => id !== fieldId)
        : [...prev, fieldId]
    );
  };

  const handleSelectAll = () => {
    setSelectedFields(filteredFields.map((f: any) => f.id));
  };

  const handleDeselectAll = () => {
    setSelectedFields([]);
  };

  const getSensitivityColor = (level: SensitivityLevel) => {
    switch (level) {
      case 'public':
        return { bg: '#E8F5E9', color: '#2E7D32', border: '#4CAF50' };
      case 'internal':
        return { bg: '#E3F2FD', color: '#1565C0', border: '#2196F3' };
      case 'confidential':
        return { bg: '#FFF3E0', color: '#E65100', border: '#FF9800' };
      case 'restricted':
        return { bg: '#FFEBEE', color: '#C62828', border: '#F44336' };
      case 'highlyRestricted':
        return { bg: '#880E4F20', color: '#880E4F', border: '#880E4F' };
    }
  };

  const getSensitivityIcon = (level: SensitivityLevel) => {
    switch (level) {
      case 'public': return '🌍';
      case 'internal': return '🏢';
      case 'confidential': return '🔒';
      case 'restricted': return '🚫';
      case 'highlyRestricted': return '⛔';
    }
  };

  const getSensitivityLabel = (level: SensitivityLevel) => {
    switch (level) {
      case 'public': return 'Public';
      case 'internal': return 'Internal';
      case 'confidential': return 'Confidential';
      case 'restricted': return 'Restricted';
      case 'highlyRestricted': return 'Highly Restricted';
    }
  };

  // Loading state
  const isLoading = isLoadingStats || isLoadingFields;

  if (isLoading) {
    return <StandardLoadingState message="Loading data sensitivity classifications..." />;
  }

  // Error state
  if (error) {
    return (
      <StandardErrorState
        title="Error loading sensitivity data"
        message="Failed to load sensitivity classifications. Please try again."
      />
    );
  }

  return (
    <div className="data-sensitivity-manager">
      {/* Developer Indicator */}
      {process.env.NODE_ENV === 'development' && fields.length > 0 && fieldsResponse?._usingMockData && (
        <div style={{
          marginBottom: '1rem',
          padding: '0.5rem 1rem',
          background: '#E3F2FD',
          borderRadius: '4px',
          fontSize: '0.875rem',
          color: '#1565C0',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <span>ℹ️</span>
          <span>Showing mock data. Connect backend to persist changes.</span>
        </div>
      )}

      {/* Header */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{ marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Data Sensitivity Management
        </h3>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
          Classify and protect sensitive data with field-level security
        </p>
      </div>

      {/* Statistics */}
      {stats && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{
            padding: '1rem',
            background: 'var(--background-secondary)',
            borderRadius: '6px',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginBottom: '0.25rem', textTransform: 'uppercase' }}>
              Total Fields
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'var(--text-primary)' }}>
              {stats.totalClassifiedFields}
            </div>
          </div>

          <div style={{
            padding: '1rem',
            background: 'rgba(76, 175, 80, 0.15)',
            borderRadius: '6px',
            border: '1px solid #4CAF50'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#4CAF50', marginBottom: '0.25rem' }}>
              🌍 PUBLIC
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#4CAF50' }}>
              {stats.byClassification.public}
            </div>
          </div>

          <div style={{
            padding: '1rem',
            background: 'rgba(33, 150, 243, 0.15)',
            borderRadius: '6px',
            border: '1px solid #2196F3'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#2196F3', marginBottom: '0.25rem' }}>
              🏢 INTERNAL
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#2196F3' }}>
              {stats.byClassification.internal}
            </div>
          </div>

          <div style={{
            padding: '1rem',
            background: 'rgba(255, 152, 0, 0.15)',
            borderRadius: '6px',
            border: '1px solid #FF9800'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#FF9800', marginBottom: '0.25rem' }}>
              🔒 CONFIDENTIAL
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#FF9800' }}>
              {stats.byClassification.confidential}
            </div>
          </div>

          <div style={{
            padding: '1rem',
            background: 'rgba(244, 67, 54, 0.15)',
            borderRadius: '6px',
            border: '1px solid #F44336'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#F44336', marginBottom: '0.25rem' }}>
              🚫 RESTRICTED
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#F44336' }}>
              {stats.byClassification.restricted}
            </div>
          </div>

          <div style={{
            padding: '1rem',
            background: 'rgba(136, 14, 79, 0.15)',
            borderRadius: '6px',
            border: '1px solid #880E4F'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#880E4F', marginBottom: '0.25rem' }}>
              ⛔ HIGHLY RESTRICTED
            </div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#880E4F' }}>
              {stats.byClassification.highlyRestricted || 0}
            </div>
          </div>
        </div>
      )}

      {/* Filters and Actions */}
      <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'center' }}>
        {/* Search */}
        <input
          type="search"
          placeholder="Search fields..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: '1 1 200px',
            minWidth: '200px',
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            fontSize: '0.875rem',
            color: 'var(--text-primary)',
            backgroundColor: 'var(--background-primary)'
          }}
        />

        {/* Object Filter */}
        <select
          value={selectedObject}
          onChange={(e) => setSelectedObject(e.target.value)}
          style={{
            width: '180px',
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            fontSize: '0.875rem',
            color: 'var(--text-primary)',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <option value="all">All Objects</option>
          {objects.map((obj: any) => (
            <option key={obj.id} value={obj.name}>{obj.label}</option>
          ))}
        </select>

        {/* Level Filter */}
        <select
          value={selectedLevel}
          onChange={(e) => setSelectedLevel(e.target.value as SensitivityLevel | 'all')}
          style={{
            width: '200px',
            padding: '0.5rem 0.75rem',
            border: '1px solid var(--border-color)',
            borderRadius: '6px',
            fontSize: '0.875rem',
            color: 'var(--text-primary)',
            backgroundColor: 'var(--background-primary)'
          }}
        >
          <option value="all">All Levels</option>
          <option value="public">🌍 Public</option>
          <option value="internal">🏢 Internal</option>
          <option value="confidential">🔒 Confidential</option>
          <option value="restricted">🚫 Restricted</option>
          <option value="highlyRestricted">⛔ Highly Restricted</option>
        </select>
      </div>

      {/* Bulk Actions Bar */}
      {selectedFields.length > 0 && (
        <div style={{
          marginBottom: '1rem',
          padding: '1rem',
          background: 'var(--primary-bg-subtle, rgba(33, 150, 243, 0.1))',
          border: '1px solid var(--primary-color)',
          borderRadius: '6px',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontWeight: 600, color: 'var(--primary-color)' }}>
            {selectedFields.length} field{selectedFields.length !== 1 ? 's' : ''} selected
          </span>

          <button
            onClick={handleBulkUpdate}
            disabled={updateMutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'var(--primary-color)',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: updateMutation.isPending ? 'not-allowed' : 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          >
            {updateMutation.isPending ? 'Updating...' : 'Set Sensitivity Level'}
          </button>

          <button
            onClick={handleDeselectAll}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: 'transparent',
              color: 'var(--text-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500,
              marginLeft: 'auto'
            }}
          >
            Deselect All
          </button>
        </div>
      )}

      {/* Selection Controls */}
      {filteredFields.length > 0 && (
        <div style={{
          marginBottom: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '0.875rem',
          color: 'var(--text-secondary)'
        }}>
          <span style={{ color: 'var(--text-primary)' }}>
            Showing {filteredFields.length} field{filteredFields.length !== 1 ? 's' : ''}
          </span>
          <button
            onClick={selectedFields.length === filteredFields.length ? handleDeselectAll : handleSelectAll}
            style={{
              padding: '0.375rem 0.75rem',
              backgroundColor: 'transparent',
              color: 'var(--primary-color)',
              border: '1px solid var(--primary-color)',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '0.875rem',
              fontWeight: 500
            }}
          >
            {selectedFields.length === filteredFields.length ? 'Deselect All' : 'Select All'}
          </button>
        </div>
      )}

      {/* Fields Table */}
      {filteredFields.length === 0 ? (
        <div style={{
          padding: '3rem',
          textAlign: 'center',
          background: 'var(--background-secondary)',
          borderRadius: '8px',
          color: 'var(--text-secondary)'
        }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem', opacity: 0.3 }}>🔒</div>
          <p>No fields found</p>
          {(searchQuery || selectedObject !== 'all' || selectedLevel !== 'all') && (
            <button
              onClick={() => {
                setSearchQuery('');
                setSelectedObject('all');
                setSelectedLevel('all');
              }}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: 'transparent',
                color: 'var(--primary-color)',
                border: '1px solid var(--primary-color)',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                fontWeight: 500
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div style={{
          border: '1px solid var(--border-color)',
          borderRadius: '8px',
          overflow: 'auto',
          maxWidth: '100%'
        }}>
          <table style={{ width: '100%', minWidth: '1200px', borderCollapse: 'collapse' }}>
            <thead style={{ background: 'var(--background-secondary)' }}>
              <tr>
                <th style={{ padding: '1rem', textAlign: 'left', width: '40px', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>
                  <input
                    type="checkbox"
                    checked={selectedFields.length === filteredFields.length}
                    onChange={selectedFields.length === filteredFields.length ? handleDeselectAll : handleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', minWidth: '250px', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>Field</th>
                <th style={{ padding: '1rem', textAlign: 'left', width: '150px', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>Object</th>
                <th style={{ padding: '1rem', textAlign: 'center', width: '180px', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>Level</th>
                <th style={{ padding: '1rem', textAlign: 'left', width: '200px', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>Access</th>
                <th style={{ padding: '1rem', textAlign: 'center', width: '100px', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>Encrypted</th>
                <th style={{ padding: '1rem', textAlign: 'center', width: '100px', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>Masking</th>
                <th style={{ padding: '1rem', textAlign: 'center', width: '120px', fontWeight: 600, fontSize: '0.875rem', color: 'var(--text-primary)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredFields.map((field: any, index: number) => {
                const colors = getSensitivityColor(field.classification as SensitivityLevel);
                const isEditing = editingId === field.id;

                return (
                  <tr
                    key={field.id}
                    style={{
                      background: selectedFields.includes(field.id)
                        ? 'rgba(33, 150, 243, 0.15)'
                        : index % 2 === 0
                        ? 'var(--background-primary)'
                        : 'var(--background-secondary)',
                      borderTop: '1px solid var(--border-color)',
                      color: 'var(--text-primary)'
                    }}
                  >
                    <td style={{ padding: '1rem', color: 'var(--text-primary)' }}>
                      <input
                        type="checkbox"
                        checked={selectedFields.includes(field.id)}
                        onChange={() => handleToggleSelection(field.id)}
                        style={{ cursor: 'pointer' }}
                      />
                    </td>
                    <td style={{ padding: '1rem', color: 'var(--text-primary)' }}>
                      <div style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: '0.875rem' }}>
                        {field.fieldName}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>
                        {field.fieldApiName}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                        {field.fieldType}
                        {field.isCustom && <span style={{ marginLeft: '0.5rem', fontStyle: 'italic' }}>(Custom)</span>}
                        {field.isRequired && <span style={{ marginLeft: '0.5rem', color: '#F44336', fontWeight: 600 }}>*</span>}
                      </div>
                    </td>
                    <td style={{ padding: '1rem', color: 'var(--text-primary)' }}>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        background: 'var(--background-secondary)',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        color: 'var(--text-primary)'
                      }}>
                        {field.objectName}
                      </span>
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-primary)' }}>
                      {isEditing ? (
                        <select
                          value={field.classification}
                          onChange={(e) => handleUpdate(field.id, { classification: e.target.value })}
                          style={{
                            padding: '0.375rem 0.5rem',
                            border: '1px solid var(--border-color)',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            color: 'var(--text-primary)',
                            backgroundColor: 'var(--background-primary)'
                          }}
                        >
                          <option value="public">🌍 Public</option>
                          <option value="internal">🏢 Internal</option>
                          <option value="confidential">🔒 Confidential</option>
                          <option value="restricted">🚫 Restricted</option>
                          <option value="highlyRestricted">⛔ Highly Restricted</option>
                        </select>
                      ) : (
                        <span style={{
                          padding: '0.375rem 0.75rem',
                          background: colors.bg,
                          color: colors.color,
                          border: `1px solid ${colors.border}`,
                          borderRadius: '4px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.375rem',
                          letterSpacing: '0.025em'
                        }}>
                          <span>{getSensitivityIcon(field.classification as SensitivityLevel)}</span>
                          <span>{getSensitivityLabel(field.classification as SensitivityLevel)}</span>
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '1rem', fontSize: '0.75rem', color: 'var(--text-primary)' }}>
                      {field.permissions.canRead.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                          {field.permissions.canRead.slice(0, 2).map((role: string) => (
                            <span key={role} style={{
                              padding: '0.125rem 0.5rem',
                              background: 'var(--background-secondary)',
                              border: '1px solid var(--border-color)',
                              borderRadius: '3px',
                              fontSize: '0.7rem',
                              color: 'var(--text-primary)'
                            }}>
                              {role}
                            </span>
                          ))}
                          {field.permissions.canRead.length > 2 && (
                            <span style={{ color: 'var(--text-secondary)', fontSize: '0.65rem', fontStyle: 'italic' }}>
                              +{field.permissions.canRead.length - 2} more
                            </span>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.75rem', fontStyle: 'italic' }}>All roles</span>
                      )}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-primary)' }}>
                      {field.isEncrypted ? (
                        <span style={{ color: '#4CAF50', fontSize: '1.25rem' }}>✓</span>
                      ) : (
                        <span style={{ color: '#9E9E9E', fontSize: '1.25rem' }}>−</span>
                      )}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-primary)' }}>
                      {field.maskingRule && field.maskingRule !== 'none' ? (
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          background: '#FFF3E0',
                          color: '#E65100',
                          border: '1px solid #FF9800',
                          borderRadius: '3px',
                          fontSize: '0.7rem',
                          fontWeight: 600,
                          textTransform: 'uppercase'
                        }}>
                          {field.maskingRule}
                        </span>
                      ) : (
                        <span style={{ color: '#9E9E9E', fontSize: '1.25rem' }}>−</span>
                      )}
                    </td>
                    <td style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-primary)' }}>
                      <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => setEditingId(null)}
                              title="Cancel"
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: 'transparent',
                                border: '1px solid var(--border-color)',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '1rem'
                              }}
                            >
                              ✕
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              title="Save changes"
                              style={{
                                padding: '0.25rem 0.5rem',
                                background: '#4CAF50',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '1rem'
                              }}
                            >
                              ✓
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setEditingId(field.id)}
                            title="Edit classification"
                            style={{
                              padding: '0.25rem 0.5rem',
                              background: 'transparent',
                              border: '1px solid var(--border-color)',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '1rem'
                            }}
                          >
                            ✏️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DataSensitivityManager;
